import { ISubmissionState } from '@/modules/submission/store/state';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { SubmissionService } from '../services';
import * as AT from './actionTypes';
import * as MT from './mutationTypes';
import * as AmDashboardMT from '@/modules/amDashboard/store/mutationsTypes';
import Investment from '@/models/Investment';
import Submission from '@/models/Submission';
import {
  DownloadErrorReportsForBatchResponse,
  FetchErrorReportsForBatchUrlResponse
} from '../types';
import { OrganisationAssociationMeta } from '@/models/OrganisationAssociation';
import { ValidationError } from '@/models/ValidationError';
import { CLEAR_FILTER_DATA } from '@/components/ui/filters/store/mutationsTypes';
import { AutoCompleteFilter } from '@/components/ui/filters/types';

/**
 * --------------------------------------------------
 *  Submission Actions
 * --------------------------------------------------
 **/
export const SubmissionActions: ISubmissionActions = {
  [AT.FETCH_INVESTMENT]: async (
    { commit },
    investmentId: string
  ): Promise<Investment> => {
    const investment: Investment = await SubmissionService.fetchInvestment(
      investmentId
    );
    return investment;
  },

  [AT.FETCH_MANAGER_ASSOCIATION_META]: async (
    { commit },
    id: string
  ): Promise<OrganisationAssociationMeta> => {
    return SubmissionService.fetchManagerAssociationMeta(id);
  },

  [AT.SUBMIT_DATA]: async ({ commit }, data: any) => {
    const response = await SubmissionService.submitTemplate(data);
    commit(AmDashboardMT.ADD_SUBMISSION_TO_SUBMISSION_REQUEST, response);
    commit(CLEAR_FILTER_DATA, {
      name: AutoCompleteFilter.MATCH_SUB_REQUEST_DROPDOWN
    });
    return response;
  },

  [AT.FETCH_SUBMISSION]: async (
    { commit },
    submissionId: string
  ): Promise<Submission> => {
    const response = await SubmissionService.fetchSubmission(submissionId);
    return response;
  },

  [AT.CHANGE_SUBMISSION_STATUS]: async (
    { commit },
    data: any
  ): Promise<Submission> => {
    const response = await SubmissionService.changeSubmissionStatus(data);
    commit(AmDashboardMT.ADD_SUBMISSION_TO_SUBMISSION_REQUEST, response);
    commit(CLEAR_FILTER_DATA, {
      name: AutoCompleteFilter.MATCH_SUB_REQUEST_DROPDOWN
    });
    return response;
  },

  [AT.REQUEST_SUBMISSION_REOPEN]: async (
    { commit },
    data: any
  ): Promise<any> => {
    const response = await SubmissionService.requestSubmissionReopen(data);
    return response;
  },

  [AT.FETCH_SUBMISSION_ERRORS]: async (
    { commit, state },
    submissionId: string
  ): Promise<ValidationError[]> => {
    if (state.errors[submissionId]) {
      return state.errors[submissionId];
    }
    const errors = await SubmissionService.fetchSubmissionErrors(submissionId);
    commit(MT.STORE_SUBMISSION_ERRORS, { submissionId, errors });
    return errors;
  },

  [AT.DOWNLOAD_ERROR_REPORT]: async (
    { commit },
    submissionId: string
  ): Promise<any> => {
    const response = await SubmissionService.downloadErrorReport(submissionId);
    return response;
  },

  [AT.STORE_BYPASSED_ERRORS]: async (
    { commit },
    data: any
  ): Promise<Submission> => {
    const response = await SubmissionService.storeBypassedErrors(data);
    return response;
  },

  [AT.FETCH_SUBMISSIONS_FOR_BATCH]: async (
    { commit },
    batchId: any
  ): Promise<Submission[]> => {
    const response = await SubmissionService.fetchSubmissionsForBatch(batchId);
    return response;
  },

  [AT.DOWNLOAD_ERROR_REPORTS_FOR_BATCH]: async (
    { commit },
    batchId: string
  ): Promise<DownloadErrorReportsForBatchResponse> => {
    const response = await SubmissionService.downloadErrorReportsForBatch(
      batchId
    );
    return response;
  },

  [AT.FETCH_ERROR_REPORTS_URL_FOR_BATCH]: async (
    { commit },
    jobId: string
  ): Promise<FetchErrorReportsForBatchUrlResponse> => {
    const response = await SubmissionService.fetchErrorReporsUrlForBatch(jobId);
    return response;
  }
};

/**
 * --------------------------------------------------
 *  Submission Action Context Interface
 * --------------------------------------------------
 **/
export interface ISubmissionActionContext
  extends ActionContext<ISubmissionState, IRootState> {}

/**
 * --------------------------------------------------
 *  Submission Actions Interface
 * --------------------------------------------------
 **/
export interface ISubmissionActions
  extends ActionTree<ISubmissionState, IRootState> {
  [AT.FETCH_INVESTMENT](
    context: ISubmissionActionContext,
    investmentId: string
  ): Promise<Investment>;

  [AT.SUBMIT_DATA](context: ISubmissionActionContext, data: any): Promise<any>;

  [AT.FETCH_SUBMISSION](
    context: ISubmissionActionContext,
    submissionId: string
  ): Promise<Submission>;

  [AT.CHANGE_SUBMISSION_STATUS](
    context: ISubmissionActionContext,
    data: any
  ): Promise<Submission>;

  [AT.FETCH_SUBMISSION_ERRORS](
    context: ISubmissionActionContext,
    submissionId: string
  ): Promise<any>;

  [AT.DOWNLOAD_ERROR_REPORT](
    context: ISubmissionActionContext,
    submissionId: string
  ): Promise<any>;

  [AT.STORE_BYPASSED_ERRORS](
    context: ISubmissionActionContext,
    data: any
  ): Promise<any>;

  [AT.FETCH_SUBMISSIONS_FOR_BATCH](
    context: ISubmissionActionContext,
    batchId: string
  ): Promise<Submission[]>;

  [AT.DOWNLOAD_ERROR_REPORTS_FOR_BATCH](
    context: ISubmissionActionContext,
    batchId: string
  ): Promise<DownloadErrorReportsForBatchResponse>;

  [AT.FETCH_ERROR_REPORTS_URL_FOR_BATCH](
    context: ISubmissionActionContext,
    jobId: string
  ): Promise<FetchErrorReportsForBatchUrlResponse>;
}
